import { useState } from "react";
import { toast } from "sonner";
import { Send } from "lucide-react";
import api, { formatApiErrorDetail, IMAGES } from "@/lib/api";

const types = ["1:1 Support", "Business", "Retreats", "Community Gatherings", "General"];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", inquiry_type: "1:1 Support", message: "" });
  const [sending, setSending] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please share your name, email, and a message.");
      return;
    }
    setSending(true);
    try {
      await api.post("/contact", form);
      toast.success("Thank you — your message has been received.");
      setForm({ name: "", email: "", inquiry_type: "1:1 Support", message: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Something went wrong.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="relative py-24 md:py-32 bg-[#f7efe2] overflow-hidden grain">
      <img src={IMAGES.sound} alt="" className="absolute inset-0 w-full h-full object-cover opacity-10 pointer-events-none" />
      <div className="max-w-2xl mx-auto px-6 relative z-10">
        <div className="text-center mb-12">
          <p className="font-accent text-sm tracking-widest-2 uppercase text-[#c2a04b] mb-4">Get in touch</p>
          <h2 className="font-display text-4xl md:text-5xl text-[#3a2630] leading-tight">Begin the conversation</h2>
          <p className="text-[#3a2630]/70 mt-5 leading-relaxed">
            Reach out about 1:1 support, business, retreats, or community gatherings.
            <span className="block text-[#9c5560] font-accent mt-2">Every offering is shared freely.</span>
          </p>
        </div>

        <form onSubmit={submit} data-testid="contact-form" className="bg-[#fbf6ec] rounded-2xl p-7 md:p-9 border border-[#e3b4b0]/50 shadow-[0_14px_45px_rgba(156,85,96,0.12)] space-y-5">
          <div className="grid sm:grid-cols-2 gap-5">
            <div>
              <label className="font-accent text-sm text-[#3a2630]/80">Your Name</label>
              <input
                data-testid="contact-name"
                value={form.name}
                onChange={update("name")}
                className="mt-2 w-full rounded-xl border border-[#e3b4b0]/60 bg-[#f7efe2] px-4 py-3 outline-none focus:border-[#b76e79] transition-colors"
                placeholder="Your name"
              />
            </div>
            <div>
              <label className="font-accent text-sm text-[#3a2630]/80">Email</label>
              <input
                data-testid="contact-email"
                type="email"
                value={form.email}
                onChange={update("email")}
                className="mt-2 w-full rounded-xl border border-[#e3b4b0]/60 bg-[#f7efe2] px-4 py-3 outline-none focus:border-[#b76e79] transition-colors"
                placeholder="you@email.com"
              />
            </div>
          </div>

          <div>
            <label className="font-accent text-sm text-[#3a2630]/80">I'm reaching out about</label>
            <select
              data-testid="contact-type"
              value={form.inquiry_type}
              onChange={update("inquiry_type")}
              className="mt-2 w-full rounded-xl border border-[#e3b4b0]/60 bg-[#f7efe2] px-4 py-3 outline-none focus:border-[#b76e79] transition-colors"
            >
              {types.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="font-accent text-sm text-[#3a2630]/80">Your Message</label>
            <textarea
              data-testid="contact-message"
              value={form.message}
              onChange={update("message")}
              rows={5}
              className="mt-2 w-full rounded-xl border border-[#e3b4b0]/60 bg-[#f7efe2] px-4 py-3 outline-none focus:border-[#b76e79] transition-colors resize-none"
              placeholder="Share what's moving in you…"
            />
          </div>

          <button
            data-testid="contact-submit"
            type="submit"
            disabled={sending}
            className="w-full font-accent tracking-wide px-8 py-3.5 rounded-full bg-[#b76e79] text-[#f7efe2] hover:bg-[#9c5560] transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
          >
            {sending ? "Sending…" : (<><Send size={18} /> Send Message</>)}
          </button>
        </form>
      </div>
    </section>
  );
}
